import { useEffect, useCallback, useRef, useMemo } from 'react';
import { apiCallStatus } from 'helpers/constants';

/**
 * Performance-optimized custom hook for automatic asset group expansion and asset selection
 * @param {string} searchBoxFilter - The current search term
 * @param {Array} assetGroupOverviewList - List of asset groups with their assets
 * @param {number|null} selectedAssetGroup - Currently selected/expanded asset group ID
 * @param {function} setSelectedAssetGroup - Function to set selected asset group
 * @param {function} handleSelectedItem - Function to handle asset selection
 * @param {object} selectedAsset - Currently selected asset
 * @param {string} [sensorIdentifierFilter] - The current Smart Sensor Identifier filter value
 * @param {string} [loadingStatus] - Loading status of assetGroupOverviewList, used to avoid
 * auto-selecting from a stale list while a new sensor identifier request is in flight
 */
export const useAssetGroupSearchExpansion = (
    searchBoxFilter,
    assetGroupOverviewList,
    selectedAssetGroup,
    setSelectedAssetGroup,
    handleSelectedItem,
    selectedAsset,
    sensorIdentifierFilter,
    loadingStatus
) => {
    const previousSearchRef = useRef('');
    const previousSensorIdentifierRef = useRef('');
    const searchTimeoutRef = useRef(null);
    const expansionTimeoutRef = useRef(null);

    // Memoize flattened asset list for better performance with large datasets
    const flattenedAssets = useMemo(() => {
        if (!Array.isArray(assetGroupOverviewList)) {
            return [];
        }

        return assetGroupOverviewList.reduce((acc, assetGroup) => {
            if (assetGroup?.assets?.length > 0) {
                for (const asset of assetGroup.assets) {
                    acc.push({
                        ...asset,
                        assetGroupId: assetGroup.assetGroupId,
                        assetGroupName: assetGroup.assetGroupName
                    });
                }
            }
            return acc;
        }, []);
    }, [assetGroupOverviewList]);

    /**
     * Find matching asset using optimized search
     * @param {string} searchTerm - Search term to match against
     * @returns {object|null} - Matching asset with asset group info or null
     */
    const findMatchingAsset = useCallback((searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            return null;
        }

        const searchLower = searchTerm.toLowerCase().trim();

        // Use Array.find for early termination - more efficient for large lists
        return flattenedAssets.find(asset => {
            const assetName = asset?.assetName?.toLowerCase() || '';
            const assetId = asset?.assetId?.toString() || '';
            const assetSerialNumber = asset?.assetSerialNumber?.toLowerCase() || '';
            return assetName.includes(searchLower) || assetId.includes(searchLower) || assetSerialNumber.includes(searchLower);
        });
    }, [flattenedAssets]);

    /**
     * Trigger asset group expansion by clicking the UI element
     * @param {number} assetGroupId - ID of asset group to expand
     */
    const triggerAssetGroupExpansion = useCallback((assetGroupId) => {
        // Clear any existing expansion timeout
        if (expansionTimeoutRef.current) {
            clearTimeout(expansionTimeoutRef.current);
        }

        expansionTimeoutRef.current = setTimeout(() => {
            // Look for asset group element using the Expandable component's id
            const assetGroupElement = document.querySelector(`[id="${assetGroupId}"]`) ||
                document.querySelector(`[data-assetgroup-id="${assetGroupId}"]`);

            if (!assetGroupElement) {
                return;
            }

            // Look for clickable elements (button, expandable title, etc.)
            const clickableElement = assetGroupElement.querySelector(
                'button, [role="button"], .expandable-title, .expandable-header'
            ) || assetGroupElement.closest('.expandable')?.querySelector(
                'button, [role="button"], .expandable-title, .expandable-header'
            );

            if (clickableElement) {
                const rect = clickableElement.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    clickableElement.click();
                }
            }
        }, 50); // Reduced timeout for faster response
    }, []);

    /**
     * Main search and expansion logic
     */
    const findAndExpandAsset = useCallback((searchTerm) => {
        // Skip if search term hasn't changed or is too short
        if (!searchTerm || searchTerm.trim().length < 2 || searchTerm === previousSearchRef.current) {
            return;
        }

        const searchLower = searchTerm.toLowerCase().trim();
        previousSearchRef.current = searchTerm;

        // Find matching asset using optimized search
        const matchingAsset = findMatchingAsset(searchLower);

        if (!matchingAsset) {
            return;
        }

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Expand asset group if not already expanded
        if (selectedAssetGroup !== matchingAsset.assetGroupId) {
            triggerAssetGroupExpansion(matchingAsset.assetGroupId);
        }

        // Select the matching asset
        if (selectedAsset?.assetId !== matchingAsset.assetId) {
            searchTimeoutRef.current = setTimeout(() => {
                handleSelectedItem(matchingAsset);
            }, 400); // Optimized delay
        }
    }, [findMatchingAsset, selectedAssetGroup, selectedAsset, triggerAssetGroupExpansion, handleSelectedItem]);

    // Clear previous search when search term becomes empty
    useEffect(() => {
        if (!searchBoxFilter || searchBoxFilter.trim() === '') {
            previousSearchRef.current = '';
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            if (expansionTimeoutRef.current) {
                clearTimeout(expansionTimeoutRef.current);
                expansionTimeoutRef.current = null;
            }
        }
    }, [searchBoxFilter]);

    // Debounced search execution with optimized delay
    useEffect(() => {
        if (searchBoxFilter && searchBoxFilter.trim().length >= 2) {
            // Reduced debounce time for better responsiveness
            const timeoutId = setTimeout(() => {
                findAndExpandAsset(searchBoxFilter);
            }, 200);

            return () => clearTimeout(timeoutId);
        }
    }, [searchBoxFilter, findAndExpandAsset]);

    // The Smart Sensor Identifier filter is resolved server-side, so there is no matching field to
    // search by here — auto-expand/select only once it has narrowed the list to a single asset.
    useEffect(() => {
        const sensorIdentifierTerm = sensorIdentifierFilter?.trim();

        if (!sensorIdentifierTerm) {
            previousSensorIdentifierRef.current = '';
            return;
        }

        if (searchBoxFilter && searchBoxFilter.trim().length > 0) {
            return;
        }

        // Wait for the debounced request to settle so we don't act on a stale (pre-filter) list.
        if (loadingStatus === apiCallStatus.LOADING) {
            return;
        }

        if (sensorIdentifierTerm === previousSensorIdentifierRef.current || flattenedAssets.length !== 1) {
            return;
        }

        previousSensorIdentifierRef.current = sensorIdentifierTerm;
        const [matchingAsset] = flattenedAssets;

        if (selectedAssetGroup !== matchingAsset.assetGroupId) {
            triggerAssetGroupExpansion(matchingAsset.assetGroupId);
        }

        if (selectedAsset?.assetId !== matchingAsset.assetId) {
            handleSelectedItem(matchingAsset);
        }
    }, [sensorIdentifierFilter, searchBoxFilter, loadingStatus, flattenedAssets, selectedAssetGroup, selectedAsset, triggerAssetGroupExpansion, handleSelectedItem]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (expansionTimeoutRef.current) {
                clearTimeout(expansionTimeoutRef.current);
            }
        };
    }, []);

    return null; // This hook doesn't render anything
};
